// UI state of the targeting criteria (website full text, website profile, hiring, managing directors).
// lookalikeSeeds and discoverySource have no control in the criteria modal; they only carry a
// chat search's values through "Suche erweitern".
export const TARGETING_FILTER_DEFAULTS = {
	fulltextQuery: '',
	fulltextMode: 'all',
	customerTypes: [],
	businessModel: [],
	offerings: [],
	specializations: '',
	serviceArea: [],
	tech: [],
	hiringActive: null,
	hiringOccupations: [],
	looseFacets: false,
	oldestCeoAge: [],
	youngestCeoAge: [],
	ceoCountMin: '',
	ceoCountMax: '',
	ownerLedOnly: false,
	lookalikeSeeds: [],
	discoverySource: [],
};

// Modifiers only refine other criteria; changing one must not re-decide the search type.
const TARGETING_MODIFIER_FIELDS = ['fulltextMode', 'looseFacets'];

export const TARGETING_CRITERIA_FIELDS = Object.keys(TARGETING_FILTER_DEFAULTS).filter(
	(field) => !TARGETING_MODIFIER_FIELDS.includes(field),
);

// The list attributes of the `website` parameter group.
const WEBSITE_LIST_FIELDS = ['customerTypes', 'businessModel', 'offerings', 'serviceArea', 'tech'];
const AGE_FIELDS = ['oldestCeoAge', 'youngestCeoAge'];

const splitTerms = (value) =>
	(value || '')
		.split(',')
		.map((term) => term.trim())
		.filter(Boolean);

const parseInteger = (value) => {
	if (value === '' || value === null || value === undefined) return undefined;
	const parsed = Number.parseInt(value, 10);
	return Number.isFinite(parsed) ? parsed : undefined;
};

const hasItems = (value) => Array.isArray(value) && value.length > 0;

/**
 * Whether any targeting criterion narrows the search; the full-text mode and the "loose" switch
 * only modify other criteria and do not count.
 * @param {Object} filters - UI filter state
 * @returns {boolean}
 */
export const hasTargetingCriteria = (filters) =>
	splitTerms(filters.fulltextQuery).length > 0 ||
	WEBSITE_LIST_FIELDS.some((field) => hasItems(filters[field])) ||
	splitTerms(filters.specializations).length > 0 ||
	filters.hiringActive === true ||
	AGE_FIELDS.some((field) => hasItems(filters[field])) ||
	[filters.ceoCountMin, filters.ceoCountMax].some((value) => parseInteger(value) !== undefined) ||
	filters.ownerLedOnly === true ||
	hasItems(filters.lookalikeSeeds) ||
	hasItems(filters.discoverySource);

const buildWebsite = (filters) => {
	const website = {};
	for (const field of WEBSITE_LIST_FIELDS) {
		if (hasItems(filters[field])) website[field] = [...filters[field]];
	}
	const specializations = splitTerms(filters.specializations);
	if (specializations.length > 0) website.specializations = specializations;
	return Object.keys(website).length > 0 ? website : null;
};

// hiringOccupations only narrows hiringActive and is never sent without it.
const buildHiring = (filters) => {
	if (filters.hiringActive !== true) return null;
	const hiring = { active: true };
	if (hasItems(filters.hiringOccupations)) hiring.occupations = [...filters.hiringOccupations];
	return hiring;
};

const buildManagement = (filters) => {
	const management = {};
	for (const field of AGE_FIELDS) {
		if (hasItems(filters[field])) management[field] = [...filters[field]];
	}
	const ceoCountMin = parseInteger(filters.ceoCountMin);
	const ceoCountMax = parseInteger(filters.ceoCountMax);
	if (ceoCountMin !== undefined) management.ceoCountMin = ceoCountMin;
	if (ceoCountMax !== undefined) management.ceoCountMax = ceoCountMax;
	if (filters.ownerLedOnly === true) management.ownerLedOnly = true;
	return Object.keys(management).length > 0 ? management : null;
};

/**
 * HR search parameters of the targeting criteria, grouped by where a value comes from; only set
 * criteria are emitted.
 *
 * @param {Object} filters - UI filter state (TARGETING_FILTER_DEFAULTS keys)
 * @returns {{
 *   fulltext?: {terms: string[], mode: 'all'|'any'},
 *   website?: Object,
 *   hiring?: {active: true, occupations?: string[]},
 *   management?: Object,
 *   discoverySource?: string[],
 *   loose?: true,
 *   lookalikeSeeds?: string[]
 * }}
 */
export const buildTargetingParameters = (filters) => {
	const params = {};
	const fulltextTerms = splitTerms(filters.fulltextQuery);
	if (fulltextTerms.length > 0) {
		params.fulltext = { terms: fulltextTerms, mode: filters.fulltextMode === 'any' ? 'any' : 'all' };
	}

	const website = buildWebsite(filters);
	if (website) {
		params.website = website;
		if (filters.looseFacets === true) params.loose = true;
	}

	const hiring = buildHiring(filters);
	if (hiring) params.hiring = hiring;

	const management = buildManagement(filters);
	if (management) params.management = management;

	if (hasItems(filters.discoverySource)) params.discoverySource = [...filters.discoverySource];
	if (hasItems(filters.lookalikeSeeds)) params.lookalikeSeeds = [...filters.lookalikeSeeds];

	return params;
};

const asList = (value) => (Array.isArray(value) ? [...value] : []);
const asNumberText = (value) => (value === null || value === undefined ? '' : String(value));

/**
 * Reverse of buildTargetingParameters: stored HR query input back to UI filter state.
 * @param {Object} input - Stored prospect query input
 * @returns {Object} Targeting filter state (TARGETING_FILTER_DEFAULTS shape)
 */
export const mapTargetingInputToFilters = (input) => {
	const website = input?.website ?? {};
	const hiring = input?.hiring ?? {};
	const management = input?.management ?? {};
	return {
		...TARGETING_FILTER_DEFAULTS,
		fulltextQuery: asList(input?.fulltext?.terms).join(', '),
		fulltextMode: input?.fulltext?.mode === 'any' ? 'any' : 'all',
		...Object.fromEntries(WEBSITE_LIST_FIELDS.map((field) => [field, asList(website[field])])),
		specializations: asList(website.specializations).join(', '),
		hiringActive: hiring.active === true ? true : null,
		hiringOccupations: asList(hiring.occupations),
		looseFacets: input?.loose === true,
		...Object.fromEntries(AGE_FIELDS.map((field) => [field, asList(management[field])])),
		ceoCountMin: asNumberText(management.ceoCountMin),
		ceoCountMax: asNumberText(management.ceoCountMax),
		ownerLedOnly: management.ownerLedOnly === true,
		lookalikeSeeds: asList(input?.lookalikeSeeds),
		discoverySource: asList(input?.discoverySource),
	};
};

const termListProblem = (terms, limit, title) => {
	if (!limit) return null;
	if (limit.maxItems !== undefined && terms.length > limit.maxItems) {
		return { title, message: `Bitte höchstens ${limit.maxItems} Begriffe angeben.` };
	}
	const minLength = limit.minLength ?? 0;
	const maxLength = limit.maxLength ?? Infinity;
	const invalidTerm = terms.find((term) => term.length < minLength || term.length > maxLength);
	if (!invalidTerm) return null;
	return {
		title,
		message: `Jeder Begriff muss zwischen ${minLength} und ${maxLength} Zeichen lang sein („${invalidTerm}").`,
	};
};

const CEO_COUNT_LABEL = '„Anzahl Geschäftsführer"';

const ceoCountProblem = (filters, limits) => {
	const ceoCountMin = parseInteger(filters.ceoCountMin);
	const ceoCountMax = parseInteger(filters.ceoCountMax);
	const bounds = [
		[ceoCountMin, limits?.ceoCountMin],
		[ceoCountMax, limits?.ceoCountMax],
	];
	const outOfBounds = bounds.find(
		([value, limit]) => value !== undefined && limit && (value < limit.min || value > limit.max),
	);
	if (outOfBounds) {
		const [, limit] = outOfBounds;
		return {
			title: 'Ungültiger Bereich',
			message: `Die Werte für ${CEO_COUNT_LABEL} müssen zwischen ${limit.min} und ${limit.max} liegen.`,
		};
	}
	if (ceoCountMin !== undefined && ceoCountMax !== undefined && ceoCountMin > ceoCountMax) {
		return {
			title: 'Ungültiger Bereich',
			message: `Der Min-Wert für ${CEO_COUNT_LABEL} darf nicht größer als der Max-Wert sein.`,
		};
	}
	return null;
};

const BOUNDED_LIST_LABELS = { offerings: 'Leistungen', hiringOccupations: 'Berufsfelder' };

const listSizeProblem = (filters, limits) => {
	const tooLong = Object.keys(BOUNDED_LIST_LABELS).find((field) => {
		const maxItems = limits?.[field]?.maxItems;
		const isSent = field !== 'hiringOccupations' || filters.hiringActive === true;
		return isSent && maxItems !== undefined && (filters[field]?.length ?? 0) > maxItems;
	});
	if (!tooLong) return null;
	const label = BOUNDED_LIST_LABELS[tooLong];
	return { title: `Zu viele ${label}`, message: `Bitte höchstens ${limits[tooLong].maxItems} ${label} wählen.` };
};

/**
 * Checks the targeting criteria before a search is counted, started or extended, against the
 * parameter bounds of the targeting vocabulary. Without bounds (vocabulary not loaded) only the
 * min/max order is checked; the backend validates the rest.
 * @param {Object} filters - UI filter state
 * @param {Object} [limits] - `limits` of the targeting vocabulary, keyed by search parameter
 * @returns {{title: string, message: string}|null} The first problem as a toast payload, or null when valid
 */
export const validateTargetingFilters = (filters, limits) =>
	termListProblem(splitTerms(filters.fulltextQuery), limits?.fulltext, 'Ungültiger Volltext') ??
	termListProblem(splitTerms(filters.specializations), limits?.specializations, 'Ungültige Spezialisierung') ??
	listSizeProblem(filters, limits) ??
	ceoCountProblem(filters, limits);
